import { ReactNode } from "react";
import {
  Box,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import { NavLink } from "react-router-dom";
import { normalizeApiUrl } from "../lib/apiUrl";
import { FlashlightLogo } from "./FlashlightLogo";
import { customPalette } from "../theme/palette";

const drawerWidth = 260;

type Props = {
  children: ReactNode;
};

const appHomeUrl = normalizeApiUrl(import.meta.env.REACT_APP_URL) ?? "/";

const navItems = [
  {
    label: "Benchmarks / Tests",
    to: "/assessments-admin/benchmarks",
    icon: <RuleOutlinedIcon fontSize="small" />,
  },
  {
    label: "Submissions",
    to: "/assessments-admin/submissions",
    icon: <AssignmentOutlinedIcon fontSize="small" />,
  },
];

export function AssessmentAdminLayout({ children }: Props) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        minHeight: "100dvh",
        height: { md: "100dvh" },
        overflow: { md: "hidden" },
      }}
    >
      <Box
        component="aside"
        sx={{
          width: { xs: "100%", md: drawerWidth },
          flex: { xs: "0 0 auto", md: `0 0 ${drawerWidth}px` },
          backgroundColor: customPalette.background.default,
          borderRight: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexDirection: { xs: "column", sm: "row", md: "column" },
          alignItems: { xs: "stretch", sm: "center", md: "stretch" },
          minHeight: { xs: "auto", md: "100dvh" },
          height: { md: "100dvh" },
          position: { xs: "static", md: "sticky" },
          top: { md: 0 },
          zIndex: 1,
        }}
      >
        <Box sx={{ px: 3, py: { xs: 2, md: 3 } }}>
          <Stack alignItems="center" spacing={1} aria-label="Flashlight360">
            <FlashlightLogo
              sx={{
                width: "100%",
              }}
            />
            <Typography
              sx={{ fontWeight: 700, color: customPalette.purple.strong }}
            >
              Assessments Admin
            </Typography>
          </Stack>
        </Box>
        <Divider
          orientation="vertical"
          flexItem
          sx={{ display: { xs: "none", sm: "block", md: "none" } }}
        />
        <Divider sx={{ display: { xs: "block", sm: "none", md: "block" } }} />
        <List
          sx={{
            flex: 1,
            display: { xs: "flex", md: "block" },
            gap: { xs: 1, md: 0 },
            px: 1.5,
            py: { xs: 1, md: 2 },
            overflowX: { xs: "auto", md: "visible" },
          }}
        >
          {navItems.map((item) => (
            <ListItemButton
              key={item.to}
              component={NavLink}
              to={item.to}
              sx={{
                borderRadius: 1,
                mb: { xs: 0, md: 0.5 },
                flex: { xs: "0 0 auto", md: "1 1 auto" },
                color: "text.secondary",
                "& .MuiListItemIcon-root": {
                  color: "inherit",
                  minWidth: 38,
                },
                "& .MuiListItemText-primary": {
                  color: "inherit",
                },
                "&.active": {
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                slotProps={{
                  primary: {
                    fontSize: 14,
                    fontWeight: 700,
                    letterSpacing: 0,
                    color: "inherit",
                  },
                }}
              />
            </ListItemButton>
          ))}
        </List>
        <Box sx={{ p: { xs: 1.5, md: 2 }, minWidth: { sm: 210, md: 0 } }}>
          <Button
            href={appHomeUrl}
            variant="outlined"
            fullWidth
            startIcon={<HomeOutlinedIcon />}
            sx={{ justifyContent: "flex-start", textTransform: "none" }}
            size="medium"
          >
            Back to Flashlight
          </Button>
        </Box>
      </Box>
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflowY: { md: "auto" },
          backgroundColor: customPalette.background.default,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
